/**
 * PlayerOverviewSection — surfaces all the rich player metrics defined in the
 * v7.6 dashboard mockup: PDS gauge + delta, percentile rank, scout views,
 * profile completion, wellness, physical metrics trend, PDS trend, matched
 * opportunities, scout activity, training consistency, recent activity.
 *
 * Engines pending (PDS v2, load mgmt, wellness check-ins, scout views) return
 * provisional values from the backend `engines_pending` array — surfaced as a
 * small "Provisional" pill in each affected card.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, BarChart, Bar,
} from 'recharts';
import api from '@/utils/api';
import {
  TrendingUp, Eye, ShieldCheck, Heart, Activity, MessageSquare,
  Calendar, Sparkles, Award, Bookmark, Loader2,
} from 'lucide-react';

const Pill = ({ children, color = 'text-zinc-400 border-zinc-700' }) => (
  <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 ${color}`}>
    {children}
  </span>
);

function GaugeCard({ score, outOf, rating, delta }) {
  const pct = Math.round((score / outOf) * 100);
  const dashOffset = 339.292 - (339.292 * pct) / 100; // circumference for r=54
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="pds-score-gauge">
      <div className="flex items-center justify-between mb-3">
        <p className="text-zinc-400 text-xs uppercase tracking-wider">PDS Score</p>
        <Pill color="text-[#A3FF00] border-[#A3FF00]/30">Live</Pill>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <p className="text-4xl font-heading font-bold text-[#A3FF00]" data-testid="pds-value">{score}</p>
          <p className="text-zinc-500 text-xs">/ {outOf}</p>
          <p className="text-zinc-300 text-sm mt-1">{rating}</p>
          <p className="text-[#A3FF00] text-xs mt-1">↑ {delta} pts vs last 30 days</p>
        </div>
        <div className="relative w-[110px] h-[110px] shrink-0">
          <svg width="110" height="110" viewBox="0 0 120 120" className="-rotate-90">
            <circle cx="60" cy="60" r="54" fill="none" stroke="#27272a" strokeWidth="10" />
            <circle cx="60" cy="60" r="54" fill="none" stroke="#A3FF00" strokeWidth="10"
                    strokeDasharray="339.292" strokeDashoffset={dashOffset} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-[#A3FF00]" />
          </div>
        </div>
      </div>
    </Card>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = 'text-[#A3FF00]' }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className={`text-3xl font-heading font-bold ${color}`}>{value}</p>
      {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
    </Card>
  );
}

export default function PlayerOverviewSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard/player-summary')
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center mb-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;
  }
  if (!data) return null;

  const { pds, profile_completion_pct, wellness_score, scout_views_30d, scout_activity,
          load_management, matched_opportunities, pds_trend, physical_metrics_trend,
          training_consistency_pct, recent_activity } = data;

  return (
    <div className="space-y-6 mb-8" data-testid="player-overview">
      {/* QUICK ACCESS — Performance Hub */}
      <Card className="bg-gradient-to-br from-[#0f1607] via-zinc-900 to-zinc-900 border-[#A3FF00]/30 p-5 flex flex-col md:flex-row items-center gap-4" data-testid="player-perf-hub-card">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#A3FF00] text-[10px] font-bold tracking-[0.2em] uppercase">XV VISION · GPS · WEARABLES</span>
            <span className="text-[10px] bg-[#A3FF00]/15 text-[#A3FF00] px-1.5 py-0.5 rounded font-bold">LIVE</span>
          </div>
          <h3 className="text-xl font-heading font-bold text-white">Performance Hub</h3>
          <p className="text-zinc-400 text-sm">Submit match footage for AI analysis, sync GPS sessions, and track your training load (ACWR).</p>
        </div>
        <a href="/performance" className="inline-flex items-center gap-2 bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-5 py-2.5 rounded-md transition-colors" data-testid="player-open-performance-hub">
          Open Performance Hub →
        </a>
      </Card>

      {/* TOP METRIC STRIP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <GaugeCard score={pds.score} outOf={pds.out_of} rating={pds.rating} delta={pds.delta_30d} />
        <MetricCard icon={Award} label="Percentile Rank" value={pds.percentile_rank} sub="Among U19 Flankers" />
        <MetricCard icon={Eye} label="Scout Views (30d)" value={scout_views_30d} sub={`${scout_activity.shortlists} on shortlists`} />
        <MetricCard icon={ShieldCheck} label="Profile Completion" value={`${profile_completion_pct}%`} sub={profile_completion_pct >= 90 ? "Almost there!" : "Keep building"} />
        <MetricCard icon={Heart} label="Wellness Score" value={`${wellness_score}%`} sub={wellness_score >= 80 ? "Good" : "Monitor"} />
      </div>

      {/* PDS COMPONENT BREAKDOWN + TREND + PHYSICAL */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-white">PDS Component Breakdown</h3>
            <Pill color="text-[#A3FF00] border-[#A3FF00]/30">PDS v2</Pill>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Profile Completion', value: pds.components.profile_completion, max: 15 },
              { label: 'Verification', value: pds.components.verification, max: 15 },
              { label: 'Physical Metrics', value: pds.components.physical_metrics, max: 10 },
              { label: 'Match Performance', value: pds.components.match_performance, max: 15 },
              { label: 'Video Evidence', value: pds.components.video_evidence, max: 10 },
              { label: 'Training Consistency', value: pds.components.training_consistency, max: 15 },
              { label: 'Wellness Compliance', value: pds.components.wellness_compliance, max: 10 },
              { label: 'Streak Bonus', value: pds.components.streak_bonus, max: 10 },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex justify-between items-center text-sm mb-1">
                  <span className="text-zinc-400">{row.label}</span>
                  <span className="text-zinc-300 font-mono">{row.value} / {row.max}</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-[#A3FF00]" style={{ width: `${(row.value / row.max) * 100}%` }} />
                </div>
              </div>
            ))}
            <div className="border-t border-zinc-800 pt-3 flex justify-between text-base">
              <span className="text-zinc-300 font-bold">Total</span>
              <span className="text-[#A3FF00] font-bold font-mono">{pds.score} / 100</span>
            </div>
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-white">PDS Trend</h3>
            <span className="text-zinc-500 text-xs">Last 7 months</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={pds_trend}>
              <defs>
                <linearGradient id="pdsg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A3FF00" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#A3FF00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Area type="monotone" dataKey="pds" stroke="#A3FF00" fillOpacity={1} fill="url(#pdsg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-white">Physical Metrics Trend</h3>
            <Pill>Provisional</Pill>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={physical_metrics_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="test" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Line type="monotone" dataKey="sprint_40m" stroke="#A3FF00" strokeWidth={2} name="Sprint 40m (s)" />
              <Line type="monotone" dataKey="vertical_cm" stroke="#3b82f6" strokeWidth={2} name="Vertical (cm)" />
              <Line type="monotone" dataKey="agility" stroke="#a855f7" strokeWidth={2} name="Agility (s)" />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-3 text-[11px] text-zinc-500 mt-1">
            <span><span className="inline-block w-2 h-2 bg-[#A3FF00] rounded-full mr-1" />Sprint</span>
            <span><span className="inline-block w-2 h-2 bg-[#3b82f6] rounded-full mr-1" />Vertical</span>
            <span><span className="inline-block w-2 h-2 bg-[#a855f7] rounded-full mr-1" />Agility</span>
          </div>
        </Card>
      </div>

      {/* MATCHED OPPS + SCOUT ACTIVITY + LOAD */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-white">Matched Opportunities</h3>
            <span className="text-zinc-500 text-xs">{matched_opportunities.length} active</span>
          </div>
          {matched_opportunities.length === 0 ? (
            <p className="text-zinc-500 text-sm">Complete your profile to unlock matched opportunities.</p>
          ) : (
            <div className="space-y-3">
              {matched_opportunities.map((o, i) => (
                <div key={`${o.name}-${o.programme}`} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg p-4 hover:border-[#A3FF00]/40 transition-colors" data-testid={`matched-opp-${i}`}>
                  <div>
                    <p className="font-bold text-white">{o.name}</p>
                    <p className="text-zinc-400 text-sm">{o.programme}</p>
                    <p className="text-zinc-500 text-xs mt-1">{o.location} · Apply by {o.deadline}</p>
                  </div>
                  <Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border border-[#A3FF00]/30 text-sm font-bold">{o.match_pct}% Match</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <h3 className="font-heading font-bold text-white mb-4">Scout Activity (30d)</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <Eye className="w-5 h-5 text-[#A3FF00] mx-auto mb-1" />
                <p className="text-2xl font-bold">{scout_activity.profile_views}</p>
                <p className="text-zinc-500 text-[10px]">Profile Views</p>
              </div>
              <div>
                <Bookmark className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <p className="text-2xl font-bold">{scout_activity.shortlists}</p>
                <p className="text-zinc-500 text-[10px]">Shortlists</p>
              </div>
              <div>
                <MessageSquare className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-2xl font-bold">{scout_activity.messages}</p>
                <p className="text-zinc-500 text-[10px]">Messages</p>
              </div>
            </div>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-white">Load Management</h3>
              <Pill>Provisional</Pill>
            </div>
            <p className="text-zinc-500 text-[11px] mb-2">Acute : Chronic Workload Ratio</p>
            <p className="text-3xl font-bold text-[#A3FF00]">{load_management.acwr}</p>
            <Badge className="mt-2 bg-[#A3FF00]/10 text-[#A3FF00] border border-[#A3FF00]/30">{load_management.rating}</Badge>
          </Card>
        </div>
      </div>

      {/* TRAINING + RECENT ACTIVITY + RECOMMENDATIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">Training Consistency</h3>
            <Sparkles className="w-4 h-4 text-[#A3FF00]" />
          </div>
          <p className="text-3xl font-bold text-[#A3FF00] mb-1">{training_consistency_pct}%</p>
          <p className="text-zinc-500 text-xs mb-3">Sessions logged this month</p>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-[#A3FF00]" style={{ width: `${training_consistency_pct}%` }} />
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3">Recent Activity</h3>
          {recent_activity.length === 0 ? (
            <p className="text-zinc-500 text-sm">No recent activity logged yet — start logging training sessions to see your timeline.</p>
          ) : (
            <ul className="space-y-2">
              {recent_activity.map((a) => (
                <li key={`${a.label}-${a.date}`} className="flex items-center gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-[#A3FF00]" />
                  <span className="text-zinc-300">{a.label}</span>
                  <span className="text-zinc-500 ml-auto text-xs">{a.date}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="bg-gradient-to-br from-[#0f1607] to-zinc-900 border-[#A3FF00]/30 p-5" data-testid="pds-recommendations">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white flex items-center gap-2">
              <Award className="w-4 h-4 text-[#A3FF00]" />Top Recommendations
            </h3>
            <Pill color="text-[#A3FF00] border-[#A3FF00]/30">PDS v2</Pill>
          </div>
          {pds.recommendations && pds.recommendations.length > 0 ? (
            <ul className="space-y-2.5">
              {pds.recommendations.map((rec, i) => (
                <li key={rec} className="flex items-start gap-2.5 text-sm" data-testid={`pds-rec-${i}`}>
                  <span className="w-5 h-5 rounded-full bg-[#A3FF00] text-black flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-zinc-200 leading-snug">{rec}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-zinc-500 text-sm">No recommendations — you&apos;re scoring well across all components.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
